import * as Yup from "yup";
import Link from "next/link";
import * as React from "react";
import { useFormik } from "formik";
import { Error } from "@/types/index";
import styles from "./styles.module.css";
import ReactMarkdown from "react-markdown";
import { Button, FormError, FormInput } from "@/components/Shared";

export const ContactForm: React.FC<{
  submitContent: string;
  legalBit: string;
  loading: boolean;
  error: Error | null;
  handleSubmit: (details: {
    firstName: string;
    lastName: string;
    email: string;
    contactNumber: string;
    broadbandOptIn: boolean;
  }) => void;
}> = ({ submitContent, legalBit, handleSubmit, loading, error }) => {
  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      contactNumber: "",
      optIn: false,
      broadbandOptIn: false,
    },
    validationSchema: Yup.object({
      firstName: Yup.string()
        .min(2, "Please enter your first name.")
        .max(30, "Your first name must be 30 characters or less.")
        .required("Please enter your first name."),
      lastName: Yup.string()
        .min(2, "Please enter your last name.")
        .max(30, "Your last name must be 30 characters or less.")
        .required("Please enter your last name."),
      email: Yup.string()
        .email("Please enter a valid email address.")
        .required("Please enter your email address."),
      contactNumber: Yup.string()
        .min(11, "Please enter a valid contact number.")
        .max(13, "Please enter a valid contact number.")
        .required("Please enter your contact number."),
      optIn: Yup.boolean()
        .oneOf([true], "You must agree to the terms and conditions.")
        .required("You must agree to the terms and conditions."),
      broadbandOptIn: Yup.boolean(),
    }),
    onSubmit: async (values: any) => {
      handleSubmit({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        contactNumber: values.contactNumber,
        broadbandOptIn: values.broadbandOptIn,
      });
    },
  });

  return (
    <div id="contact-form" className="container-sm-fixed mt-3">
      <form onSubmit={formik.handleSubmit}>
        <div className="col-md-8 col-12 mx-auto">
          {error && <FormError message={error.message} theme="dark" />}
          <div className="mb-3">
            <FormInput
              name="firstName"
              value={formik.values.firstName}
              handleChange={formik.handleChange}
              placeholder="First name..."
              isRequired={true}
            />
            {formik.touched.firstName && formik.errors.firstName ? (
              <FormError
                message="Please enter a valid first name."
                theme="dark"
              />
            ) : null}
          </div>
          <div className="mb-3">
            <FormInput
              name="lastName"
              value={formik.values.lastName}
              handleChange={formik.handleChange}
              placeholder="Last name..."
              isRequired={true}
            />
            {formik.touched.lastName && formik.errors.lastName ? (
              <FormError
                message="Please enter a valid last name."
                theme="dark"
              />
            ) : null}
          </div>
          <div className="mb-3">
            <FormInput
              name="email"
              value={formik.values.email}
              handleChange={formik.handleChange}
              placeholder="Email..."
              isRequired={true}
            />
            {formik.touched.email && formik.errors.email ? (
              <FormError message="Please enter a valid email." theme="dark" />
            ) : null}
          </div>
          <div className="mb-3">
            <FormInput
              name="contactNumber"
              value={formik.values.contactNumber}
              handleChange={formik.handleChange}
              placeholder="Contact Number..."
              isRequired={true}
            />
            {formik.touched.contactNumber && formik.errors.contactNumber ? (
              <FormError
                message="Please enter a valid contact number."
                theme="dark"
              />
            ) : null}
          </div>
          <div className="mb-4 mt-4 mx-auto ">
            <label>
              <input
                type="checkbox"
                name="optIn"
                checked={formik.values.optIn}
                onChange={formik.handleChange}
                className="me-2"
              />
              By ticking this box and submitting your details, you confirm that
              you are over 18 years old and agree to be contacted by us or our
              partners. You also consent to your data being processed in
              accordance with our{" "}
              <Link href={"/legal/privacy-policy"} target="_blank">
                Privacy Policy
              </Link>{" "}
              .
            </label>
            {formik.touched.optIn && formik.errors.optIn && (
              <FormError
                message={"You must tick the box to continue."}
                theme="dark"
              />
            )}
            <label className="mt-3">
              <input
                type="checkbox"
                name="broadbandOptIn"
                checked={formik.values.broadbandOptIn}
                onChange={formik.handleChange}
                className="me-2"
              />
              If you would also like to receive the best deals on Broadband from Consumer Choices, please tick this box.
            </label>
          </div>
          <Button
            type="submit"
            name="button"
            title={submitContent}
            isLoading={loading}
          />
          <div className={`mt-3 mx-2 text-center ${styles["legal-bit"]}`}>
            <ReactMarkdown>{legalBit}</ReactMarkdown>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
